"use client"

import { useState } from "react"
import { usePathname } from "next/navigation"
import { Menu, X, Instagram, Facebook, Youtube, Linkedin } from "lucide-react"
import { motion, AnimatePresence } from "framer-motion"

const navLinks = [
  { label: "BOOKING SERVICES", icon: <img src="/images/nav-calendar.png" alt="" className="w-5 h-5" />, href: "/programs/booking" },
  { label: "BULLY TALK HUB", icon: <img src="/images/nav-message.png" alt="" className="w-5 h-5" />, href: "/hub" },
  { label: "DONATE", icon: <img src="/images/nav-users.png" alt="" className="w-5 h-5" />, href: "/donate" },
  { label: "VOLUNTEER", icon: <img src="/images/nav-volunteer.png" alt="" className="w-5 h-5" />, href: "/volunteer" },
  { label: "ABOUT", icon: <img src="/images/nav-heart.png" alt="" className="w-5 h-5" />, href: "/about" },
]

export default function Header() {
  const [mobileOpen, setMobileOpen] = useState(false)
  const pathname = usePathname()

  const isActive = (href: string) => {
    if (href === "/") return pathname === "/"
    return pathname.startsWith(href)
  }

  return (
    <div className="fixed top-0 left-0 right-0 z-50 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-stretch h-[96px]">
          {/* LEFT — Dedicated logo container */}
          <div className="flex items-center pr-3 lg:pr-4 border-r border-slate-200 p-2">
            <a href="/" className="shrink-0 flex items-center">
              <img src="/images/bullytalk-logo.webp" alt="Bully Talk Logo" className="h-[80px] w-auto" />
            </a>
          </div>

          {/* RIGHT — Everything else */}
          <div className="flex-1 flex flex-col min-w-0">
            {/* Top Row: Tagline + FOLLOW US + Social */}
            <div className="flex items-center justify-between h-[48px] border-b border-slate-100 px-4">
              <div className="hidden md:flex items-center gap-3">
                <span className="relative inline-block">
                  <span className="font-heading text-lg xl:text-xl text-black tracking-wider rotate-[-2deg] block pt-0.5">
                    SPEAK UP. REACH OUT. BE KIND.
                  </span>
                  <svg className="absolute -bottom-1 left-0 w-full h-2 text-violet" viewBox="0 0 180 8" preserveAspectRatio="none">
                    <path d="M0 6C30 1 60 7 90 4C120 1 150 7 180 3" stroke="currentColor" strokeWidth="3" strokeLinecap="round" fill="none"/>
                  </svg>
                </span>
              </div>

              <div className="flex items-center gap-3 ml-auto">
                <span className="hidden sm:block text-base font-antonio tracking-tight text-black uppercase">FOLLOW US</span>
                <div className="hidden sm:flex items-center gap-2">
                  <a href="https://www.instagram.com/bullytalkinc/" target="_blank" rel="noopener noreferrer" className="w-8 h-8 rounded-full bg-black flex items-center justify-center text-white hover:bg-violet hover:text-white transition-colors"><Instagram className="w-4 h-4" /></a>
                  <a href="https://www.facebook.com/profile.php?id=100076543013149" target="_blank" rel="noopener noreferrer" className="w-8 h-8 rounded-full bg-black flex items-center justify-center text-white hover:bg-violet hover:text-white transition-colors"><Facebook className="w-4 h-4" /></a>
                  <a href="https://www.youtube.com/@bullytalkinc" target="_blank" rel="noopener noreferrer" className="w-8 h-8 rounded-full bg-black flex items-center justify-center text-white hover:bg-violet hover:text-white transition-colors"><Youtube className="w-4 h-4" /></a>
                  <a href="https://www.linkedin.com/company/bullytalk-inc-/" target="_blank" rel="noopener noreferrer" className="w-8 h-8 rounded-full bg-black flex items-center justify-center text-white hover:bg-violet hover:text-white transition-colors"><Linkedin className="w-4 h-4" /></a>
                </div>
              </div>
            </div>

            {/* Bottom Row: Nav Links */}
            <div className="flex h-[48px]">
              <nav className="hidden lg:flex items-stretch flex-1 h-full">
                {navLinks.map((link) => (
                  <a
                    key={link.label}
                    href={link.href}
                    className={`flex-1 relative flex items-center justify-center gap-2.5 text-sm font-antonio font-bold tracking-wider uppercase whitespace-nowrap border-r last:border-r-0 border-slate-200 transition-all duration-200 group ${
                      isActive(link.href)
                        ? "text-violet bg-violet/[0.04]"
                        : "text-black hover:text-violet hover:bg-violet/[0.04]"
                    }`}
                  >
                    {/* Underline — visible when active, shown on hover otherwise */}
                    <span className={`absolute bottom-0 left-2 right-2 h-[4px] transition-colors duration-200 ${
                      isActive(link.href) ? "bg-violet" : "bg-transparent group-hover:bg-violet"
                    }`} />
                    <span className={`transition-colors duration-200 ${
                      isActive(link.href) ? "text-violet" : "text-black/60 group-hover:text-violet"
                    }`}>
                      {link.icon}
                    </span>
                    {link.label}
                  </a>
                ))}
              </nav>

              {/* Mobile toggle */}
              <button
                className="lg:hidden absolute right-4 p-2 text-black bg-slate-100 rounded-md"
                onClick={() => setMobileOpen(!mobileOpen)}
              >
                {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden overflow-hidden bg-white border-t border-slate-100"
          >
            <div className="px-4 py-6 space-y-4 max-w-sm mx-auto">
              {navLinks.map((link) => (
                <a
                  key={link.label}
                  href={link.href}
                  className={`flex items-center gap-3 text-base font-antonio font-bold py-3 px-4 bg-slate-50 rounded-md uppercase transition-colors border-l-[4px] ${
                    isActive(link.href)
                      ? "text-violet border-l-violet"
                      : "text-black hover:text-violet border-l-transparent hover:border-l-violet"
                  }`}
                  onClick={() => setMobileOpen(false)}
                >
                  <span className="text-black/60 group-hover:text-violet transition-colors duration-200">{link.icon}</span>
                  {link.label}
                </a>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}
